import React, { useMemo } from 'react';
import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { DialogActionButton, WO2Modal } from 'src/common';
import { MFA_LOCKED_MESSAGE, MfaDialogState } from 'src/common/store/mfa';

export interface Props {
  open: boolean;
  state: MfaDialogState;
  errorMessage: string;
  attemptsRemaining: number | null;
  onCancel: () => void;
  onRetry: () => void;
}

export const MfaDialog: React.FC<Props> = ({ open, state, errorMessage, attemptsRemaining, onCancel, onRetry }) => {
  const theme = useTheme();

  const actions: DialogActionButton[] = useMemo(() => {
    switch (state) {
      case 'verifying':
        return [{ label: 'Cancel', onClickHandler: onCancel, type: 'outlined' }];
      case 'success':
      case 'locked':
        return [{ label: 'Close', onClickHandler: onCancel, type: 'outlined' }];
      default:
        return [
          { label: 'Cancel', onClickHandler: onCancel, type: 'outlined' },
          { label: 'Retry', onClickHandler: onRetry, type: 'contained' },
        ];
    }
  }, [state, onCancel, onRetry]);

  return (
    <WO2Modal
      MuiDialogProps={{
        open,
        onClose: () => {
          if (state !== 'verifying' && state !== 'success') onCancel();
        },
        maxWidth: 'xs',
      }}
      title={<b>Step-Up Verification</b>}
      actions={actions}
      maxWidth="xs"
    >
      <Box display="flex" flexDirection="column" alignItems="center" padding="16px 0" minHeight="160px" justifyContent="center">
        {state === 'verifying' && (
          <Box display="flex" flexDirection="column" gap={2} justifyContent="center" alignItems="center">
            <CircularProgress size={40} />
            <Typography variant="body1" fontWeight={500}>
              Complete verification in the popup window...
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Enter your authenticator code when prompted.
            </Typography>
          </Box>
        )}
        {state === 'success' && (
          <Box display="flex" flexDirection="column" gap={2} justifyContent="center" alignItems="center">
            <CheckCircleOutlineIcon sx={{ color: theme.palette.success.main, fontSize: '48px !important' }} />
            <Typography variant="body1" fontWeight={500}>
              Verified successfully
            </Typography>
          </Box>
        )}
        {state === 'error' && (
          <Box display="flex" flexDirection="column" gap={2} justifyContent="center" alignItems="center">
            <ErrorOutlineIcon sx={{ color: theme.palette.error.main, fontSize: '48px !important' }} />
            <Typography variant="body1" fontWeight={500} color="error">
              {errorMessage}
            </Typography>
            {attemptsRemaining !== null && attemptsRemaining > 0 && (
              <Typography variant="body2" color="textSecondary">
                {attemptsRemaining} attempt{attemptsRemaining === 1 ? '' : 's'} remaining.
              </Typography>
            )}
          </Box>
        )}
        {state === 'locked' && (
          <Box display="flex" flexDirection="column" gap={2} justifyContent="center" alignItems="center">
            <ErrorOutlineIcon sx={{ color: theme.palette.error.main, fontSize: '48px !important' }} />
            <Typography variant="body1" fontWeight={500} color="error">
              Account locked
            </Typography>
            <Typography variant="body2" color="textSecondary" textAlign="center">
              {errorMessage || MFA_LOCKED_MESSAGE}
            </Typography>
          </Box>
        )}
      </Box>
    </WO2Modal>
  );
};
